import { useCallback, useEffect, useMemo, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";

import { PaymentLimitsService } from "../services/paymentLimits.service";
import type { AmountLimitCell } from "../types/paymentLimits.types";

export interface LimitKpis {
  totalCells: number;
  /** Corridors with at least one effective bound in the current scope. */
  limitedCells: number;
  cellsWithOverrides: number;
}

export interface UseLimitSettingsResult {
  cells: AmountLimitCell[];
  kpis: LimitKpis;
  isLoading: boolean;
  isRefreshing: boolean;
  hasError: boolean;
  /** A 401/403 latches for the session: the whole surface becomes a no-access view. */
  hasAccess: boolean;
  refresh: () => void;
}

/**
 * State of the amount-limits page, scoped to one organization or to the global view (`organizationId` null).
 * Same freshness contract as the rails board: no polling — limits change by human action only; data reloads
 * on the manual button and after every successful mutation. Loading is derived from the attempt key so a
 * scope change brings the skeletons back instead of leaving the previous scope's matrix under live actions.
 */
export function useLimitSettings(organizationId: string | null): UseLimitSettingsResult {
  const [cells, setCells] = useState<AmountLimitCell[]>([]);
  const [hasError, setHasError] = useState(false);
  const [hasAccess, setHasAccess] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [settledKey, setSettledKey] = useState<string | null>(null);
  const attemptKey = `${reloadKey}:${organizationId ?? ""}`;
  const isLoading = settledKey !== attemptKey && !isRefreshing;

  useEffect(() => {
    let cancelled = false;
    PaymentLimitsService.list(organizationId)
      .then((response) => {
        if (cancelled) return;
        setCells(response.limits ?? []);
        setHasError(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (HttpApiService.isAccessDeniedError(error)) setHasAccess(false);
        else setHasError(true);
      })
      .finally(() => {
        if (cancelled) return;
        setSettledKey(attemptKey);
        setIsRefreshing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [attemptKey, organizationId]);

  const refresh = useCallback(() => {
    setIsRefreshing(true);
    setReloadKey((key) => key + 1);
  }, []);

  const kpis = useMemo<LimitKpis>(
    () => ({
      totalCells: cells.length,
      limitedCells: cells.filter((cell) =>
        cell.effective.some((limit) => limit.min_amount !== null || limit.max_amount !== null),
      ).length,
      cellsWithOverrides: cells.filter((cell) => cell.org_overrides.length > 0).length,
    }),
    [cells],
  );

  return { cells, kpis, isLoading, isRefreshing, hasError, hasAccess, refresh };
}
